import Vue from "vue";
import {aggregateOriginalQuantities} from "@/utils/orderAvailability";

const editOrderModule = {
    namespaced: true,
    persistent: true,
    state: {
        orderId: null,
        data: {},
        originalQuantities: {}
    },
    mutations: {
        setOrderId(state, orderId) {
            state.orderId = orderId;
        },
        setData(state, payload) {
            const data = payload && Object.prototype.hasOwnProperty.call(payload, "data")
                ? payload.data
                : payload;
            const selectedUserId = payload?.userId;
            const selectedWarehouseId = payload?.warehouseId;
            const addressExternalId = payload?.addressExternalId;
            const originalProducts = payload?.originalProducts || [];
            const originalQuantities = aggregateOriginalQuantities(originalProducts);
            const normalizedData = {};

            Object.keys(data || {}).forEach(userId => {
                if (selectedUserId != null && Number(userId) !== Number(selectedUserId)) {
                    return;
                }
                normalizedData[userId] = {};
                Object.keys(data[userId]).forEach(warehouseId => {
                    if (selectedWarehouseId != null
                        && Number(warehouseId) !== Number(selectedWarehouseId)) {
                        return;
                    }
                    const warehouseData = data[userId][warehouseId];
                    const recipients = Array.isArray(warehouseData._recipients)
                        ? warehouseData._recipients
                        : [];
                    const normalizedWarehouseData = {
                        _recipients: recipients
                            .filter(recipient => addressExternalId == null
                            || Number(recipient.address_id) === Number(addressExternalId))
                            .slice(0, 1)
                            .map(recipient => ({...recipient}))
                    };

                    Object.keys(warehouseData).forEach(productExternalId => {
                        const product = warehouseData[productExternalId];
                        if (!product || product.external_id == null) {
                            return;
                        }
                        normalizedWarehouseData[productExternalId] = {
                            ...product,
                            original_quantity: originalQuantities[product.external_id] ?? 0
                        };
                    });
                    normalizedData[userId][warehouseId] = normalizedWarehouseData;
                });
            });

            state.originalQuantities = originalQuantities;
            state.data = normalizedData;
        },
        addProductToShoppingCart(state, payload) {
            let userId = payload.userId;
            let warehouseId = payload.warehouseId;
            let productExternalId = payload.product.external_id;

            if (!state.data[userId]) {
                Vue.set(state.data, userId, {});
            }

            if (!state.data[userId][warehouseId]) {
                Vue.set(state.data[userId], warehouseId, {});
            }

            const existingProduct = state.data[userId][warehouseId][productExternalId];
            Vue.set(state.data[userId][warehouseId], productExternalId, {
                ...payload.product,
                original_quantity: existingProduct?.original_quantity
                    ?? state.originalQuantities?.[productExternalId]
                    ?? 0
            });
        },
        addRecipientToShoppingCart(state, payload) {
            let userId = payload.userId;
            let warehouseId = payload.warehouseId;

            if (!state.data[userId]) {
                Vue.set(state.data, userId, {});
            }

            if (!state.data[userId][warehouseId]) {
                Vue.set(state.data[userId], warehouseId, {});
            }

            Vue.set(state.data[userId][warehouseId], "_recipients", [payload.recipient]);
        },
        removeProductFromCart(state, payload) {
            let userId = payload.userId;
            let warehouseId = payload.warehouseId;
            let productExternalId = payload.productExternalId;
            if (userId in state.data && warehouseId in state.data[userId] && productExternalId in state.data[userId][warehouseId]) {
                Vue.delete(state.data[userId][warehouseId], productExternalId);
            }
        },
        removeProductsFromCart(state, payload) {
            let userId = payload.userId;
            let warehouseId = payload.warehouseId;
            if (userId in state.data && warehouseId in state.data[userId]) {
                Vue.delete(state.data[userId], warehouseId);
            }
        },
        removeRecipientFromCart(state, payload) {
            let userId = payload.userId;
            let warehouseId = payload.warehouseId;
            let addressId = payload.addressId;
            if (userId in state.data && warehouseId in state.data[userId]) {
                let recipient = state.data[userId][warehouseId]["_recipients"]
                    ?.find(recipient => recipient.address_id === addressId);
                if (recipient) {
                    const index = state.data[userId][warehouseId]["_recipients"].indexOf(recipient);
                    state.data[userId][warehouseId]["_recipients"].splice(index, 1);
                }
            }
        },
        reset(state) {
            state.data = {};
            state.originalQuantities = {};
            state.orderId = null;
        }
    },
    getters: {
        getOrderId: state => {
            return state.orderId;
        },
        getData: state => {
            return state.data;
        },
        getProductsInShoppingCart: state => userId => {
            return state.data[userId];
        },
        getRecipientsInShoppingCart: state => userId => {
            return state.data[userId];
        }
    }
}

export default editOrderModule;
